import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Chart from "react-apexcharts";
import { observer } from "mobx-react";
import Lottie from "react-lottie";
import reportsStore from "../reportsStore";
import { userDetail } from "Stores/userDetail";
import dialerStoreData from "Stores/DialerData";
import Utils from "Utils/utils";
import EmptyReport from "Pages/Dashboard/reports/EmptyReport";
import NoActivityFound from "Pages/Dashboard/reports/NoActivityFound";
import BarChartLoader from "Utils/LottieFiles/BarChart.json";

const options = {
  chart: {
    type: "bar",
    height: 350,
    stacked: true,
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    }
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: {
          position: "bottom",
          offsetX: -10,
          offsetY: 0
        }
      }
    }
  ],
  yaxis: {
    labels: {
      formatter: value => {
        return Math.floor(value);
      }
    }
  },
  dataLabels: {
    enabled: false
  },
  plotOptions: {
    bar: {
      borderRadius: 0,
      horizontal: false
    }
  },
  legend: {
    position: "top",
    horizontalAlign: "right"
  },
  fill: {
    opacity: 1
  }
};

const CallDataInsightsWrapper = observer(() => {
  let location = useLocation();
  let contactAdded = userDetail?.userFeatureFlag?.noOfContactsAddedInTeam;
  let stageActiveList = [...dialerStoreData?.stageList || []].filter(
    item => item?.displayEnabled
  );

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: BarChartLoader
  };

  let series = [];
  let colors = [];

  let iteration = [...stageActiveList].forEach(ele => {
    const { displayName: name = "", color } = ele || {};
    // Ensure color is never null
    const safeColor = color || "#CCCCCC";
    colors.push(safeColor);
    series.push({ name, data: [] });
  });

  // Unified state for atomic updates (matching CallDataInsights)
  const [loader, setLoader] = useState(reportsStore.callsAnalyticsLoader);
  const [chartConfig, setChartConfig] = useState({
    series: series,
    options: options
  });

  const constructChartData = (list = []) => {
    if (list) {
      const tempData = []; 
      
      // Initialize series structure
      [...stageActiveList].forEach(ele => {
        const { displayName: name = "" } = ele || {};
        tempData.push({ name, data: [] });
      });

      // Re-map data first to check which dates have actual data
      const outcomeMap = new Map();
      list.forEach(item => {
          if(item.date && item.outcome && typeof item.outcome === 'object') {
              outcomeMap.set(item.date, item.outcome);
          }
      });

      const tempXaxisDate = [];
      
      // Collect only dates that have at least one non-zero outcome value
      outcomeMap.forEach((outcome, date) => {
          // Check if this date has any non-zero data across all stages
          let hasData = false;
          if (outcome && typeof outcome === 'object') {
              [...stageActiveList].forEach((stageItem) => {
                  const val = Math.ceil(outcome[stageItem?.displayName] || 0);
                  if (val > 0) {
                      hasData = true;
                  }
              });
          }
          
          // Only include dates with actual data
          if (hasData && date) {
              tempXaxisDate.push(date);
          }
      });

      // Get date filter info - match CallDataInsights structure
      // Note: reportsStore.dateFilter uses timestamps, so we need to convert
      const {
        fromDate: fromDateTimestamp = "",
        toDate: toDateTimestamp = ""
      } = reportsStore?.dateFilter || {};
      
      // Helper to prevent UTC shift for date-only strings (e.g. "2025-01-01")
      const parseDateSafe = (val) => {
          if (!val) return null;
          // If string is YYYY-MM-DD (length 10) without time, force Local Midnight
          if (typeof val === 'string' && val.length === 10 && val.indexOf('-') !== -1) {
             return new Date(val + "T00:00:00");
          }
          return new Date(val);
      };

      // Convert timestamps to Date objects for formatting
      const fromDateObj = parseDateSafe(fromDateTimestamp);
      const toDateObj = parseDateSafe(toDateTimestamp);
      
      // Calculate days difference - matching CallDataInsights behavior
      const days = fromDateObj && toDateObj ? Utils.getDateDiffNumber(fromDateObj, toDateObj) : 0;
      const isMultiDay = days > 1;
      
      // Sort dates - only dates with actual data are included
      tempXaxisDate.sort(); 

       const finalCategories = [];
       
       tempXaxisDate.forEach(dateStr => {
           if (!dateStr || dateStr === "--") return;

           // Format category - format dates properly to avoid showing time
           let displayCategory;
           if (isMultiDay) {
               // Format date string for display (e.g., "2025-01-15" -> "15 Jan")
               const dateObj = parseDateSafe(dateStr);
               if (dateObj) {
                   // Format as "dd MMM" to match datetimeFormatter format
                   const day = dateObj.getDate();
                   const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                   const month = monthNames[dateObj.getMonth()];
                   displayCategory = `${day} ${month}`;
               } else {
                   displayCategory = dateStr;
               }
           } else {
               const formatted = Utils.formatDate(dateStr);
               if (!formatted || formatted === "--") return;
               displayCategory = formatted;
           }
           finalCategories.push(displayCategory);

           // Add data for each series
           const outcome = outcomeMap.get(dateStr) || {};
           [...stageActiveList].forEach((stageItem, index) => {
               const val = Math.ceil(outcome[stageItem?.displayName] || 0);
               tempData[index].data.push(val); 
           });
       });

      // Atomic Update
      setChartConfig({
        series: tempData,
        options: {
            ...options,
            xaxis: {
                ...options.xaxis,
                type: "category", // Use category type to avoid time display issues
                categories: finalCategories,
                labels: {
                    // No datetimeFormatter needed for category type
                }
            },
            plotOptions: {
              bar: {
                columnWidth: (days > 20 && "40%") || "20%"
              }
            },
            colors
        }
      });
    }
  };

  useEffect(() => {
    constructChartData(reportsStore.callsAnalyticsData);
  }, [reportsStore.callsAnalyticsData]);

  useEffect(() => {
    setLoader(reportsStore.callsAnalyticsLoader);
  }, [reportsStore.callsAnalyticsLoader]);

  const { series: chartData, options: chartOptions } = chartConfig;

  return (
    <div className="chartCard">
      <h4 className="cardTitle">Insights</h4>
      <div className="chartContent">
        {(!loader &&
          contactAdded &&
          chartData?.length &&
          chartData[0]?.data?.length && (
            <div className="stackedBarChart">
              <Chart
                options={chartOptions}
                series={chartData}
                type="bar"
                height={350}
              />
            </div>
          )) ||
          ""}
        {loader && (
          <div className="chartLoader">
            <Lottie
              options={defaultOptions}
              width={200}
              style={{ margin: "0 auto" }}
              speed={1}
            />
          </div>
        )}
        {!loader && !contactAdded && (
          <EmptyReport
            pathname={location?.pathname}
            className="emptyEmailInsight"
          />
        )}
        {(!loader && chartData?.length && !chartData[0]?.data?.length && (
          <NoActivityFound />
        )) ||
          ""}
      </div>
    </div>
  );
});

const CallsAnalyticsSection = observer(() => {
  useEffect(() => {
    // Fetch analytics data when component mounts or filters change
    if (reportsStore.dateFilter) {
      reportsStore.fetchCallsAnalytics();
    }
  }, [reportsStore.dateFilter, reportsStore.memberIds]);

  return (
    <div className="callsAnalytics">
      <div className="wrapCallsAnalyticsRow">
        <CallDataInsightsWrapper />
      </div>
    </div>
  );
});

export default CallsAnalyticsSection;
export { CallsAnalyticsSection };
